import React, { useState } from "react";
import { Box, Grid, Typography, Paper, Modal, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

function GallerySection() {
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  // Using temporary images from the reference website
  // Instructions for user will be provided on how to replace these
  const galleryImages = [
    {
      url: "/250mm.jpg",
      title: "Product Display 1",
    }
  ];

  const handlePrevImage = () => {
    setSelectedImageIndex((prev) =>
      prev === 0 ? galleryImages.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setSelectedImageIndex((prev) =>
      prev === galleryImages.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <Box
      sx={{
        background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
        py: 8,
        px: 2,
      }}
    >
      <Grid container justifyContent="center">
        <Grid item xs={12} md={10} lg={9}>
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h4"
              fontWeight="700"
              sx={{
                color: "#0f172a",
                mb: 2,
                fontSize: { xs: "1.8rem", sm: "2rem" },
              }}
            >
              Gallery
            </Typography>
            <Box
              sx={{
                width: "60px",
                height: "4px",
                background: "linear-gradient(90deg, #3b82f6 0%, #06b6d4 100%)",
                borderRadius: "2px",
              }}
            />
            <Typography
              sx={{
                fontSize: "0.95rem",
                color: "#64748b",
                mt: 2,
              }}
            >
              Explore our products and services in action
            </Typography>
          </Box>

          {/* Gallery Grid */}
          <Grid container spacing={3}>
            {galleryImages.map((image, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper
                  elevation={0}
                  onClick={() => setSelectedImageIndex(index)}
                  sx={{
                    background: "white",
                    borderRadius: "16px",
                    overflow: "hidden",
                    border: "1px solid #e2e8f0",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    height: "280px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 12px 30px rgba(59, 130, 246, 0.25)",
                      borderColor: "#3b82f6",
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={image.url}
                    alt={image.title}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      transition: "transform 0.3s ease",
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = "scale(1.05)";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Lightbox Modal */}
          <Modal
            open={selectedImageIndex !== null}
            onClose={() => setSelectedImageIndex(null)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                position: "relative",
                maxWidth: "90vw",
                maxHeight: "90vh",
                outline: "none",
              }}
            >
              {/* Close Button */}
              <IconButton
                onClick={() => setSelectedImageIndex(null)}
                sx={{
                  position: "absolute",
                  top: -50,
                  right: 0,
                  color: "white",
                  "&:hover": {
                    background: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: "2rem" }} />
              </IconButton>

              {/* Main Image */}
              <Paper
                elevation={0}
                sx={{
                  background: "white",
                  borderRadius: "16px",
                  overflow: "hidden",
                  maxWidth: "100%",
                  maxHeight: "80vh",
                }}
              >
                {selectedImageIndex !== null && (
                  <Box
                    component="img"
                    src={galleryImages[selectedImageIndex].url}
                    alt={galleryImages[selectedImageIndex].title}
                    sx={{
                      width: "100%",
                      height: "auto",
                      maxHeight: "80vh",
                      objectFit: "contain",
                    }}
                  />
                )}
              </Paper>

              {/* Navigation Buttons */}
              <IconButton
                onClick={handlePrevImage}
                sx={{
                  position: "absolute",
                  left: -60,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "white",
                  background: "rgba(59, 130, 246, 0.5)",
                  "&:hover": {
                    background: "rgba(59, 130, 246, 0.8)",
                  },
                }}
              >
                <NavigateBeforeIcon sx={{ fontSize: "2rem" }} />
              </IconButton>

              <IconButton
                onClick={handleNextImage}
                sx={{
                  position: "absolute",
                  right: -60,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "white",
                  background: "rgba(59, 130, 246, 0.5)",
                  "&:hover": {
                    background: "rgba(59, 130, 246, 0.8)",
                  },
                }}
              >
                <NavigateNextIcon sx={{ fontSize: "2rem" }} />
              </IconButton>

              {/* Image Counter */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: -40,
                  left: "50%",
                  transform: "translateX(-50%)",
                  color: "white",
                  fontSize: "1rem",
                  fontWeight: "600",
                }}
              >
                {selectedImageIndex !== null && (
                  <>
                    {selectedImageIndex + 1} / {galleryImages.length}
                  </>
                )}
              </Box>
            </Box>
          </Modal>
        </Grid>
      </Grid>
    </Box>
  );
}

export default GallerySection;
